import React, { FC, useState, useEffect, useCallback, useRef } from "react";
import {
  Card,
  Table,
  Button,
  Space,
  Tag,
  notification,
  Modal,
  Upload,
  Typography,
  Input,
  Select,
  message,
  Pagination
} from "antd";
import {
  ReloadOutlined,
  UploadOutlined,
  DeleteOutlined,
  LinkOutlined,
  DownloadOutlined,
  ArrowRightOutlined,
  SearchOutlined,
  FileTextOutlined
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import AppHeader from '../components/AppHeader';
import PageLayout from '../components/PageLayout';
import { fullname, role as roleKey, email as emailKey } from '../utils/data';
import { getAuthToken } from '../utils/functions';
import { ReferencesUrl } from '../utils/network';
import axios from 'axios';
import type { UploadProps } from 'antd';
import ArrowRight from "../components/svg/ArrowRight";
import ArrowRightWhite from "../components/svg/ArrowRightWhite";
import EyeIcon from "../components/svg/EyeIcon";
import CurvDown from "../components/svg/CurvDown";


const { Title, Text } = Typography;
const { Option } = Select;

interface Reference {
  id: number;
  case_name_citation: string;
  year: number;
  legal_issue: string | null;
  opinion_url: string | null;
  basis: string | null;
  basis_display: string | null;
  case_type: string | null;
  case_name: string | null;
  one_sentence_rule: string | null;
  created_by_name: string;
  created_at: string;
  updated_at: string;
}

interface ParsedReference {
  case_name_citation: string;
  year: number;
  legal_issue: string | null;
  opinion_url: string | null;
  basis: string | null;
  case_type: string | null;
  case_name: string | null;
  one_sentence_rule: string | null;
  rowNumber: number;
  errors: string[];
  isValid: boolean;
}

const References: FC = () => {
  const navigate = useNavigate();
  const [references, setReferences] = useState<Reference[]>([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [filters, setFilters] = useState<{ basis?: string; year?: number; case_type?: string }>({});
  const [searchText, setSearchText] = useState('');
  const [pageSize, setPageSize] = useState(20);
  const [previewModalVisible, setPreviewModalVisible] = useState(false);
  const [parsedData, setParsedData] = useState<ParsedReference[]>([]);
  const [uploadFile, setUploadFile] = useState<File | null>(null);
  const [validating, setValidating] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const tableContainerRef = useRef<HTMLDivElement>(null);
  const [scrollInfo, setScrollInfo] = useState({ left: 0, width: 0, client: 0 });

  // Get current user info from localStorage
  const currentUser = {
    role: localStorage.getItem(roleKey) || 'user',
    fullname: localStorage.getItem(fullname) || 'User',
    email: localStorage.getItem(emailKey) || ''
  };

  // Restrict access to admin users only
  useEffect(() => {
    if (!currentUser.role || currentUser.role.toLowerCase() !== 'admin') {
      navigate('/');
    }
  }, [currentUser.role, navigate]);

  // Fetch references
  const fetchReferences = useCallback(async () => {
    setLoading(true);
    try {
      const headers = getAuthToken();
      if (!headers) {
        notification.error({
          message: 'Error',
          description: 'Authentication required',
        });
        return;
      }

      let url = ReferencesUrl;
      const params = new URLSearchParams();

      if (filters.basis) {
        params.append('basis', filters.basis);
      }
      if (filters.case_type) {
        params.append('case_type', filters.case_type);
      }
      if (filters.year) {
        params.append('year', filters.year.toString());
      }
      if (searchText) {
        params.append('search', searchText);
      }

      if (params.toString()) {
        url += `?${params.toString()}`;
      }

      const response = await axios.get(url, headers);
      setReferences(response.data);
    } catch (error: any) {
      notification.error({
        message: 'Error',
        description: error.response?.data?.detail || 'Failed to fetch references',
      });
    } finally {
      setLoading(false);
    }
  }, [filters, searchText]);

  useEffect(() => {
    fetchReferences();
  }, [fetchReferences]);

  const pagedData = React.useMemo(() => references.slice(
    (currentPage - 1) * pageSize,
    currentPage * pageSize
  ), [references, currentPage, pageSize]);

  // Custom Scrollbar Sync Logic
  useEffect(() => {
    const tableBody = tableContainerRef.current?.querySelector('.ant-table-content');
    if (!tableBody) return;

    const onScroll = () => {
      setScrollInfo({
        left: tableBody.scrollLeft,
        width: tableBody.scrollWidth,
        client: tableBody.clientWidth
      });
    };

    tableBody.addEventListener('scroll', onScroll);
    onScroll(); // Initial sync

    window.addEventListener('resize', onScroll);
    
    return () => {
      tableBody.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
    };
  }, [references, loading, pagedData]); 

  // Parse CSV file - Simple parsing without validation
  const parseCSV = (content: string): ParsedReference[] => {
    const lines = content.split('\n').filter(line => line.trim() && !line.trim().startsWith('#'));
    if (lines.length < 2) {
      throw new Error('CSV file must have at least a header row and one data row');
    }

    // Parse header row
    const headerLine = lines[0];
    // Handle CSV with quotes and commas inside fields
    const headers: string[] = [];
    let currentHeader = '';
    let inQuotes = false;

    for (let i = 0; i < headerLine.length; i++) {
      const char = headerLine[i];
      if (char === '"') {
        inQuotes = !inQuotes;
      } else if (char === ',' && !inQuotes) {
        headers.push(currentHeader.trim().replace(/^"|"$/g, ''));
        currentHeader = '';
      } else {
        currentHeader += char;
      }
    }
    headers.push(currentHeader.trim().replace(/^"|"$/g, ''));

    // Find column indices (case-insensitive)
    const caseNameIndex = headers.findIndex(h =>
      h.toLowerCase().includes('case') && (h.toLowerCase().includes('name') || h.toLowerCase().includes('citation'))
    );
    const yearIndex = headers.findIndex(h => h.toLowerCase().includes('year'));
    const legalIssueIndex = headers.findIndex(h =>
      h.toLowerCase().includes('legal') && h.toLowerCase().includes('issue')
    );
    const opinionUrlIndex = headers.findIndex(h =>
      h.toLowerCase().includes('opinion') && h.toLowerCase().includes('url')
    );
    const basisIndex = headers.findIndex(h => h.toLowerCase().includes('basis'));
    const caseTypeIndex = headers.findIndex(h =>
      h.toLowerCase().includes('case') && h.toLowerCase().includes('type')
    );
    const caseNameFallbackIndex = headers.findIndex(h => h.toLowerCase() === 'case name' || h.toLowerCase() === 'case_name');
    const oneSentenceRuleIndex = headers.findIndex(h =>
      h.toLowerCase().includes('one') && h.toLowerCase().includes('sentence') && h.toLowerCase().includes('rule')
    );

    if (caseNameIndex === -1 || yearIndex === -1) {
      throw new Error('CSV must contain "Case Name / Citation" and "Year" columns');
    }

    // Parse data rows
    const parsed: ParsedReference[] = [];
    for (let i = 1; i < lines.length; i++) {
      const line = lines[i];

      // Handle CSV with quotes and commas inside fields
      const values: string[] = [];
      let currentValue = '';
      let inQuotes = false;

      for (let j = 0; j < line.length; j++) {
        const char = line[j];
        if (char === '"') {
          inQuotes = !inQuotes;
        } else if (char === ',' && !inQuotes) {
          values.push(currentValue.trim().replace(/^"|"$/g, ''));
          currentValue = '';
        } else {
          currentValue += char;
        }
      }
      values.push(currentValue.trim().replace(/^"|"$/g, ''));

      // Extract values
      const caseName = caseNameIndex >= 0 ? (values[caseNameIndex] || '') : '';
      const yearStr = yearIndex >= 0 ? (values[yearIndex] || '') : '';
      // Try to parse year, but don't validate - just use the string if parsing fails
      let year = 0;
      if (yearStr) {
        const parsedYear = parseInt(yearStr.trim());
        year = isNaN(parsedYear) ? 0 : parsedYear;
      }

      const legalIssue = legalIssueIndex >= 0 ? (values[legalIssueIndex] || null) : null;
      const opinionUrl = opinionUrlIndex >= 0 ? (values[opinionUrlIndex] || null) : null;
      const basis = basisIndex >= 0 ? (values[basisIndex] || null) : null;
      const caseType = caseTypeIndex >= 0 ? (values[caseTypeIndex] || null) : null;
      const caseNameFallback = caseNameFallbackIndex >= 0 ? (values[caseNameFallbackIndex] || null) : null;
      const oneSentenceRule = oneSentenceRuleIndex >= 0 ? (values[oneSentenceRuleIndex] || null) : null;

      parsed.push({
        case_name_citation: caseName,
        year: year || 0,
        legal_issue: legalIssue,
        opinion_url: opinionUrl,
        basis,
        case_type: caseType,
        case_name: caseNameFallback,
        one_sentence_rule: oneSentenceRule,
        rowNumber: i + 1,
        errors: [], // No validation errors
        isValid: true, // All rows are considered valid for display
      });
    }

    return parsed;
  };

  // Handle file parsing and preview
  const handleFileParse = async (file: File) => {
    setValidating(true);
    try {
      const fileName = file.name.toLowerCase();

      if (fileName.endsWith('.csv')) {
        // Parse CSV
        const reader = new FileReader();
        reader.onload = (e) => {
          try {
            const content = e.target?.result as string;
            const parsed = parseCSV(content);
            setParsedData(parsed);
            setUploadFile(file);
            setPreviewModalVisible(true);
          } catch (error: any) {
            notification.error({
              message: 'Parse Error',
              description: error.message || 'Failed to parse CSV file',
            });
          } finally {
            setValidating(false);
          }
        };
        reader.onerror = () => {
          notification.error({
            message: 'File Read Error',
            description: 'Failed to read the file',
          });
          setValidating(false);
        };
        reader.readAsText(file);
      } else if (fileName.endsWith('.xlsx') || fileName.endsWith('.xls')) {
        // For Excel files, we'll need to upload to backend for parsing
        // Or we can add xlsx library. For now, show a message
        notification.warning({
          message: 'Excel File Detected',
          description: 'Excel file parsing will be handled on the server. Please proceed with upload.',
          duration: 3,
        });
        // For Excel, directly upload (backend handles parsing)
        handleDirectUpload(file);
        setValidating(false);
      } else {
        notification.error({
          message: 'Invalid File Type',
          description: 'Please upload a CSV or Excel file',
        });
        setValidating(false);
      }
    } catch (error: any) {
      notification.error({
        message: 'Error',
        description: error.message || 'Failed to process file',
      });
      setValidating(false);
    }
  };

  // Handle direct upload (for Excel or when user confirms CSV)
  const handleDirectUpload = async (file: File) => {
    setUploading(true);
    try {
      const headers = getAuthToken();
      if (!headers) {
        notification.error({
          message: 'Error',
          description: 'Authentication required',
        });
        return;
      }

      const formData = new FormData();
      formData.append('file', file);

      const response = await axios.post(
        `${ReferencesUrl}upload/`,
        formData,
        {
          headers: {
            ...headers.headers,
          },
        }
      );

      if (response.data.success) {
        notification.success({
          message: 'Upload Successful',
          description: response.data.message || `Created: ${response.data.created}, Errors: ${response.data.errors}`,
          duration: 5,
        });

        // Show error details if any
        if (response.data.error_details && response.data.error_details.length > 0) {
          console.error('Upload errors:', response.data.error_details);
          Modal.warning({
            title: 'Upload Completed with Errors',
            content: (
              <div>
                <p>{response.data.message}</p>
                <ul style={{ maxHeight: '300px', overflowY: 'auto' }}>
                  {response.data.error_details.slice(0, 20).map((error: string, index: number) => (
                    <li key={index} style={{ fontSize: '12px', marginBottom: '4px' }}>{error}</li>
                  ))}
                  {response.data.error_details.length > 20 && (
                    <li style={{ fontSize: '12px', color: '#999' }}>
                      ... and {response.data.error_details.length - 20} more errors
                    </li>
                  )}
                </ul>
              </div>
            ),
            width: 600,
          });
        }

        fetchReferences();
        setPreviewModalVisible(false);
        setParsedData([]);
        setUploadFile(null);
      } else {
        // Show detailed error information
        const errorMessage = response.data.message || 'Failed to upload file';
        const errorDetails = response.data.error_details || [];

        notification.error({
          message: 'Upload Failed',
          description: errorMessage,
          duration: 8,
        });

        // Show error details modal if there are specific errors
        if (errorDetails.length > 0) {
          Modal.error({
            title: 'Upload Failed - Error Details',
            content: (
              <div>
                <p>{errorMessage}</p>
                <p><strong>Created: {response.data.created || 0} records</strong></p>
                <p><strong>Errors: {response.data.errors || 0} rows</strong></p>
                <ul style={{ maxHeight: '300px', overflowY: 'auto', marginTop: '16px' }}>
                  {errorDetails.slice(0, 30).map((error: string, index: number) => (
                    <li key={index} style={{ fontSize: '12px', marginBottom: '4px', color: '#ff4d4f' }}>{error}</li>
                  ))}
                  {errorDetails.length > 30 && (
                    <li style={{ fontSize: '12px', color: '#999' }}>
                      ... and {errorDetails.length - 30} more errors
                    </li>
                  )}
                </ul>
              </div>
            ),
            width: 700,
          });
        }
      }
    } catch (error: any) {
      notification.error({
        message: 'Upload Error',
        description: error.response?.data?.message || 'Failed to upload file. Please try again.',
      });
    } finally {
      setUploading(false);
    }
  };

  // Handle file upload
  const uploadProps: UploadProps = {
    name: 'file',
    accept: '.csv,.xlsx,.xls',
    showUploadList: false,
    beforeUpload: (file) => {
      const isValidType = file.type === 'text/csv' ||
        file.name.endsWith('.csv') ||
        file.name.endsWith('.xlsx') ||
        file.name.endsWith('.xls');
      if (!isValidType) {
        message.error('Please upload a CSV or Excel file');
        return false;
      }
      handleFileParse(file);
      return false; // Prevent auto upload
    },
  };

  // Handle confirm upload from preview modal
  const handleConfirmUpload = () => {
    if (!uploadFile) return;

    if (parsedData.length === 0) {
      notification.warning({
        message: 'No Data',
        description: 'No data rows found in the file',
      });
      return;
    }

    // Upload the file - backend will handle validation
    handleDirectUpload(uploadFile);
  };

  // Download CSV template
  const handleDownloadTemplate = () => {
    // Create CSV content with headers and sample row
    const headers = [
      'Case Name / Citation',
      'Year',
      'Legal Issue',
      'Opinion URL',
      'Basis',
      'Case Type'
    ];

    // Sample row with examples
    const sampleRow = [
      'Smith v. Company, 123 F.3d 456 (2020)',
      '2020',
      'Age discrimination in hiring',
      'https://example.com/opinion.pdf',
      'Age',
      'termination'
    ];

    // Create CSV content
    const csvContent = [
      headers.join(','),
      sampleRow.join(','),
      '', // Empty line
      '# Instructions:',
      '# 1. Case Name / Citation: Required - Full case name and citation',
      '# 2. Year: Required - Year the case was decided (e.g., 2020)',
      '# 3. Legal Issue: Optional - Brief description of the legal issue',
      '# 4. Opinion URL: Optional - URL to the full legal opinion',
      '# 5. Basis: Optional - Discrimination basis (All, Age, Color, Mental/Physical Handicap, National Origin, Race, Religion, Reprisal, Sex)',
      '# 6. Case Type: Optional - Type of case (accomadation, different treatment, harassment, nonselection, sexual harassment, termination)',
      '#',
      '# Note: Remove the instruction lines (lines starting with #) before uploading'
    ].join('\n');

    // Create blob and download
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);

    link.setAttribute('href', url);
    link.setAttribute('download', 'references_template.csv');
    link.style.visibility = 'hidden';

    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    notification.success({
      message: 'Template Downloaded',
      description: 'CSV template has been downloaded. Fill in your data and upload.',
      duration: 3,
    });
  };

  // Handle delete
  const handleDelete = async (id: number) => {
    Modal.confirm({
      title: 'Delete Reference',
      content: 'Are you sure you want to delete this reference?',
      okText: 'Delete',
      okType: 'danger',
      onOk: async () => {
        try {
          const headers = getAuthToken();
          if (!headers) {
            notification.error({
              message: 'Error',
              description: 'Authentication required',
            });
            return;
          }

          await axios.delete(`${ReferencesUrl}${id}/`, headers);
          notification.success({
            message: 'Success',
            description: 'Reference deleted successfully',
          });
          fetchReferences();
        } catch (error: any) {
          notification.error({
            message: 'Error',
            description: error.response?.data?.detail || 'Failed to delete reference',
          });
        }
      },
    });
  };

  // Columns for table
  const columns = [
    {
      title: 'Case Name / Citation',
      dataIndex: 'case_name_citation',
      key: 'case_name_citation',
      width: '25%',
      render: (text: string | null, record: Reference) => text || record.case_name || 'N/A',
    },
    {
      title: 'Year',
      dataIndex: 'year',
      key: 'year',
      width: '8%',
      sorter: (a: Reference, b: Reference) => a.year - b.year,
    },
    {
      title: 'Legal Issue',
      dataIndex: 'legal_issue',
      key: 'legal_issue',
      width: '25%',
      render: (text: string | null, record: Reference) => text || record.one_sentence_rule || 'N/A',
    },
    {
      title: 'Basis',
      dataIndex: 'basis_display',
      key: 'basis',
      width: '12%',
      render: (text: string | null, record: Reference) => {
        const val = text || record.basis;
        if (!val) return '-';
        return (
          <Tag 
            className="rounded-full px-3 py-0.5 border-none font-medium"
            style={{ 
              backgroundColor: val === 'Age' ? '#fef3c7' : val === 'Color' ? '#dcfce7' : '#f3e8ff',
              color: val === 'Age' ? '#92400e' : val === 'Color' ? '#166534' : '#6b21a8'
            }}
          >
            {val}
          </Tag>
        );
      },
      filters: [
        { text: 'All', value: 'All' },
        { text: 'Age', value: 'Age' },
        { text: 'Color', value: 'Color' },
        { text: 'Mental/Physical Handicap', value: 'Mental/Physical Handicap' },
        { text: 'National Origin', value: 'National Origin' },
        { text: 'Race', value: 'Race' },
        { text: 'Religion', value: 'Religion' },
        { text: 'Reprisal', value: 'Reprisal' },
        { text: 'Sex', value: 'Sex' },
      ],
      onFilter: (value: any, record: Reference) => record.basis === value,
    },
    {
      title: 'Case Type',
      dataIndex: 'case_type',
      key: 'case_type',
      width: '10%',
      render: (text: string | null) => text ? (
        <Tag 
          className="rounded-full px-3 py-0.5 border-none font-medium"
          style={{ backgroundColor: '#ecfdf5', color: '#065f46' }}
        >
          {text}
        </Tag>
      ) : 'N/A',
    }, 
    {
      title: 'Opinion URL',
      dataIndex: 'opinion_url',
      key: 'opinion_url',
      width: '10%',
      render: (url: string | null) =>
        url ? (
          <Button
            type="primary"
            className="flex items-center justify-center gap-[5px] pl-[3px] pr-[9px] py-[3px] rounded-full font-normal text-[13px] bg-[#4682B4]"
            onClick={() => window.open(url, 'blank')}
          >
            <EyeIcon style={{ fontSize: '20px', display: 'flex', alignItems: 'center' }} /> View
          </Button>
        ) : '-',
    },
    {
      title: 'Actions',
      key: 'actions',
      width: '10%',
      render: (_: any, record: Reference) => (
        <Button
          type="text"
          danger
          icon={<DeleteOutlined />}
          onClick={() => handleDelete(record.id)}
          className="flex items-center justify-center gap-[5px] px-[8px] py-[3px] rounded-full font-normal text-[13px] border-none bg-red-50"
        >
          Delete
        </Button>
      ),
    },
  ];

  return (
    <PageLayout>
      <AppHeader currentUser={currentUser} />

      <div className="relative z-10 w-full max-w-[1440px] mx-auto px-6 md:px-12 mt-12 md:mt-12 pb-12">
        {/* Combined Header and Filters Card */}
        <Card
          className="premium-gradient-card white mb-6 overflow-hidden rounded-[17px] shadow-sm bg-white"
          bodyStyle={{ padding: '24px 41px' }}
        >
          {/* Header Section */}
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-6 mb-8">
            <div className="flex items-center gap-4">
              <div className="bg-[#4682B4]/10 p-3 rounded-xl">
                <FileTextOutlined className="text-[#4682B4] text-2xl" />
              </div>
              <div>
                <h1 className="!m-0 font-['Lexend',sans-serif] font-semibold text-[#1f2a37] text-[14px] md:text-[20px] leading-tight">
                  References
                </h1>
                <p className="text-[#64748b] font-['DM_Sans',sans-serif] text-[12px] md:text-[14px] mt-1 font-normal">
                  Manage legal case references and citations
                </p>
              </div>
            </div>
            <Space wrap size={16}>
              <Button
                icon={<ReloadOutlined />}
                className="flex items-center gap-2 rounded-lg h-10 px-5 font-medium border-[#e5e7eb] hover:!text-[#4682B4] hover:!border-[#4682B4]"
                onClick={fetchReferences}
                loading={loading}
              >
                Refresh
              </Button>
              <Button
                icon={<DownloadOutlined />}
                className="flex items-center gap-2 rounded-lg h-10 px-5 font-medium border-[#e5e7eb] hover:!text-[#4682B4] hover:!border-[#4682B4]"
                onClick={handleDownloadTemplate}
              >
                Template
              </Button>
              <Upload {...uploadProps}>
                <Button
                  type="primary"
                  icon={<UploadOutlined />}
                  className="bg-[#4682B4] border-none flex items-center gap-2 rounded-lg h-10 px-6 font-medium hover:!bg-[#4682B4]/90"
                  loading={uploading || validating}
                >
                  {validating ? 'Validating...' : 'Upload Data'}
                </Button>
              </Upload>
            </Space>
          </div>

          {/* Filters Section */}
          <div className="flex flex-wrap items-end gap-x-8 gap-y-6">
            <div className="flex flex-col gap-2 min-w-[300px] flex-1">
              <label className="font-['Lexend',sans-serif] font-semibold text-[#1f2a37] text-[14px]">Search</label>
              <Input
                placeholder="Name, Legal Issue"
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                className="h-11 rounded-lg border-[#e5e7eb] hover:border-[#4682B4] focus:border-[#4682B4]"
                prefix={<SearchOutlined className="text-[#9ca3af]" />}
                allowClear
              />
            </div>
            
            <div className="flex flex-col gap-2 min-w-[200px]">
              <label className="font-['Lexend',sans-serif] font-semibold text-[#1f2a37] text-[14px]">Basis</label>
              <Select
                placeholder="Select Basis"
                allowClear
                className="h-11 w-full [&>.ant-select-selector]:!rounded-lg [&>.ant-select-selector]:!border-[#e5e7eb] hover:[&>.ant-select-selector]:!border-[#4682B4]"
                suffixIcon={<CurvDown className="pt-[10px] md:pt-[14px]" />}
                value={filters.basis}

                onChange={(value) => setFilters({ ...filters, basis: value || undefined })}
              >
                <Option value="Age">Age</Option>
                <Option value="Color">Color</Option>
                <Option value="Mental/Physical Handicap">Handicap</Option>
                <Option value="National Origin">National Origin</Option>
                <Option value="Race">Race</Option>
                <Option value="Religion">Religion</Option>
                <Option value="Reprisal">Reprisal</Option>
                <Option value="Sex">Sex</Option>
              </Select>
            </div>

            <div className="flex flex-col gap-2 min-w-[200px]">
              <label className="font-['Lexend',sans-serif] font-semibold text-[#1f2a37] text-[14px]">Case Type</label>
              <Select
                placeholder="Select Type"
                allowClear
                className="h-11 w-full [&>.ant-select-selector]:!rounded-lg [&>.ant-select-selector]:!border-[#e5e7eb] hover:[&>.ant-select-selector]:!border-[#4682B4]"
                suffixIcon={<CurvDown className="pt-[10px] md:pt-[14px]" />}
                value={filters.case_type}

                onChange={(value) => setFilters({ ...filters, case_type: value || undefined })}
              >
                <Option value="accomadation">Accomadation</Option>
                <Option value="different treatment">Different Treatment</Option>
                <Option value="harassment">Harassment</Option>
                <Option value="nonselection">Nonselection</Option>
                <Option value="sexual harassment">Sexual Harassment</Option>
                <Option value="termination">Termination</Option>
              </Select>
            </div>

            <div className="flex flex-col gap-2 w-[120px]">
              <label className="font-['Lexend',sans-serif] font-semibold text-[#1f2a37] text-[14px]">Year</label>
              <Input
                placeholder="Year"
                type="number"
                value={filters.year}
                className="h-11 rounded-lg border-[#e5e7eb] hover:border-[#4682B4]"
                onChange={(e) => {
                  const year = e.target.value ? parseInt(e.target.value) : undefined;
                  setFilters({ ...filters, year });
                }}
              />
            </div>

            <div className="flex justify-between md:justify-start gap-2 md:gap-[24px] mt-[29px] md:mt-0">
              <Button 
                className="pr-[14px] pl-[6px] py-[5px] md:pr-[25px] md:pl-[9px] md:py-[10px] rounded-full bg-[#4682B4] hover:bg-[#3b6d96] text-white font-['Lexend',sans-serif] font-normal md:font-medium text-[12px] md:text-[16px] flex items-center gap-[10px] md:gap-[13px] border-none cursor-pointer transition-colors h-[32px] md:h-[47px]"
                onClick={fetchReferences}
              >
                 <div className="w-[22px] h-[22px] md:w-[29px] md:h-[29px] rounded-full bg-white flex items-center justify-center text-[#4682B4]">
                  <div className="scale-[0.6] md:scale-100 flex items-center justify-center"><ArrowRight /></div>
                  </div>
                Search
              </Button>
              <Button 
                className="pr-[14px] pl-[6px] py-[5px] md:pr-[25px] md:pl-[9px] md:py-[10px] rounded-full bg-white border border-[#4682B4] text-[#1F2A37] font-['Lexend',sans-serif] font-normal md:font-medium text-[12px] md:text-[16px] flex items-center gap-[10px] md:gap-[13px] cursor-pointer transition-colors hover:bg-[#f8FAFC] h-[32px] md:h-[47px]"
                onClick={() => {
                  setSearchText('');
                  setFilters({});
                }}
              >
                <div className="w-[22px] h-[22px] md:w-[29px] md:h-[29px] flex items-center justify-center">
                  <div className="scale-[0.73] md:scale-100 flex items-center justify-center origin-center"><ArrowRightWhite /></div>
                </div>
                Clear
              </Button>
            </div>
          </div>
        </Card>

        {/* Table Card */}
        <style dangerouslySetInnerHTML={{__html: `
          .modern-table .ant-table-thead > tr > th {
            background: #4682B417 !important;
            color: #1F2A37 !important;
            font-weight: 400 !important;
            font-family: 'Lexend', sans-serif !important;
            border-bottom: none !important;
            padding: 16px !important;
            font-size: 10px !important;
          }
          @media (min-width: 768px) {
            .modern-table .ant-table-thead > tr > th {
              font-size: 14px !important;
            }
          }
          .modern-table .ant-table {
            background: transparent !important;
          }
          .modern-table .ant-table-container {
            background: transparent !important;
          }
          .modern-table .ant-table-row {
            transition: all 0.2s;
            background: transparent !important;
          }
          .modern-table .ant-table-row:hover {
            background-color: #F8FAFC !important;
          }
          .modern-table .ant-table-cell {
            border-bottom: 1px solid #F1F5F9 !important;
            padding: 16px !important;
            font-family: 'Lexend', sans-serif !important;
            background: transparent !important;
            font-size: 14px !important;
          }
          .modern-table .ant-table-content::-webkit-scrollbar {
            display: none !important;
          }
          .modern-table .ant-table-content {
            -ms-overflow-style: none !important;
            scrollbar-width: none !important;
          }
        `}} />
        <Card
          className="premium-gradient-card rounded-[17px] overflow-hidden "
          bodyStyle={{ padding: '16px' }}
        >
          <div ref={tableContainerRef}>
            <Table
              columns={columns}
              dataSource={pagedData}
              loading={loading}
              rowKey="id"
              className="modern-table bg-[#F8FAFC]"
              pagination={false}
              scroll={{ x: 1000 }}
            />
          </div>
        </Card>

        {/* Custom Synchronized Scrollbar Outside the card */}
        {scrollInfo.width > scrollInfo.client && (
          <div className="w-full flex justify-center mt-[19px]">
            <div 
              className="relative h-[4px] rounded-[3px] overflow-hidden" 
              style={{ 
                width: '85.90%', 
                backgroundColor: '#4682B430' 
              }}
            >
              <div 
                className="absolute h-full bg-[#4682B4] rounded-[3px] transition-all duration-100"
                style={{
                  width: `${(scrollInfo.client / scrollInfo.width) * 60}%`,
                  left: `${(scrollInfo.left / (scrollInfo.width - scrollInfo.client || 1)) * (100 - (scrollInfo.client / scrollInfo.width * 60))}%`
                }}
              />
            </div>
          </div>
        )}

        {/* Separate Pagination outside the card */}
        <div className="w-full flex justify-end mt-[22px] md:mt-[42px]">
          <style dangerouslySetInnerHTML={{__html: `
            .custom-pagination-container {
              display: flex !important;
              align-items: center !important;
              justify-content: flex-end !important;
              width: 100% !important;
            }
            .custom-pagination-container .ant-pagination-total-text {
              order: 1 !important;
              color: #555D67 !important;
              font-family: 'Lexend', sans-serif !important;
              font-size: 10px !important;
              font-weight: 300 !important;
              margin-right: 8px !important;
            }
            @media (min-width: 768px) {
              .custom-pagination-container .ant-pagination-total-text {
                font-size: 14px !important;
                margin-right: 12px !important;
              }
            }
            /* Arrows Styling */
            .custom-pagination-container .ant-pagination-prev,
            .custom-pagination-container .ant-pagination-next {
              order: 2 !important;
              border: none !important;
              background: transparent !important;
              min-width: 20px !important;
              height: 32px !important;
              display: flex !important;
              align-items: center !important;
              justify-content: center !important;
              color: #1F2A37 !important;
              font-size: 16px !important;
              margin: 0 !important;
            }
            /* Page Item & Jump (Dots) Styling */
            .custom-pagination-container .ant-pagination-item,
            .custom-pagination-container .ant-pagination-jump-prev,
            .custom-pagination-container .ant-pagination-jump-next {
              order: 2 !important;
              border-radius: 4px !important;
              height: 28px !important;
              min-width: 28px !important;
              line-height: 26px !important;
              background: white !important;
              font-family: 'Lexend', sans-serif !important;
              margin: 0 4px !important;
              font-size: 14px !important;
              display: inline-flex !important;
              align-items: center !important;
              justify-content: center !important;
            }
            .custom-pagination-container .ant-pagination-item {
              border: 0.8px solid #4682B4 !important;
            }
            .custom-pagination-container .ant-pagination-jump-prev,
            .custom-pagination-container .ant-pagination-jump-next {
              border: none !important;
            }
            /* Hide non-active items on small screens */
            @media (max-width: 767px) {
              .custom-pagination-container .ant-pagination-item:not(.ant-pagination-item-active),
              .custom-pagination-container .ant-pagination-jump-prev,
              .custom-pagination-container .ant-pagination-jump-next {
                display: none !important;
              }
            }
            .custom-pagination-container .ant-pagination-item-active {
              border-color: #4682B4 !important;
            }
            .custom-pagination-container .ant-pagination-item-active a {
              color: #1F2A37 !important;
              font-weight: 500 !important;
            }
            /* Options/Page Changer Styling */
            .custom-pagination-container .ant-pagination-options {
              order: 3 !important;
              margin-left: 8px !important;
              display: block !important; /* Ensure it is shown on mobile */
            }
            .custom-pagination-container .ant-select-selector {
              background-color: #032B440D !important;
              border: none !important;
              border-radius: 4px !important;
              height: 28px !important;
              font-family: 'Lexend', sans-serif !important;
              font-size: 12px !important;
              font-weight: 300 !important;
              color: #1F2A37 !important;
              padding: 0 10px !important;
              display: flex !important;
              align-items: center !important;
            }
            .custom-pagination-container .ant-select-selection-item {
              line-height: 28px !important;
              color: #1F2A37 !important;
            }
            .custom-pagination-container .ant-select-arrow {
              color: #1F2A37 !important;
            }
          `}} />
          <Pagination
            className="custom-pagination-container"
            current={currentPage}
            pageSize={pageSize}
            total={references.length}
            onChange={(page, size) => {
              setCurrentPage(page);
              setPageSize(size);
            }}
            showSizeChanger
            showTotal={(total) => `Total ${total} references`}
            itemRender={(page, type, originalElement) => {
              if (type === 'prev') return <span className="cursor-pointer font-bold px-1">{"<"}</span>;
              if (type === 'next') return <span className="cursor-pointer font-bold px-1">{">"}</span>;
              return originalElement;
            }}
          />
        </div>
      </div>

      {/* Preview Modal */}
      <Modal
        title="Review Upload Data"
        open={previewModalVisible}
        onCancel={() => {
          setPreviewModalVisible(false);
          setParsedData([]);
          setUploadFile(null);
        }}
        width={1000}
        footer={[
          <Button
            key="cancel"
            onClick={() => {
              setPreviewModalVisible(false);
              setParsedData([]);
              setUploadFile(null);
            }}
          >
            Cancel
          </Button>,
          <Button
            key="submit"
            type="primary"
            onClick={handleConfirmUpload}
            loading={uploading}
            disabled={parsedData.length === 0}
          >
            Submit ({parsedData.length} rows)
          </Button>,
        ]}
      >
        <div style={{ marginBottom: '16px' }}>
          <Space>
            <Text strong>Total Rows:</Text>
            <Tag>{parsedData.length}</Tag>
            <Text type="secondary">Review the data below and click Submit to upload</Text>
          </Space>
        </div>
        <Table
          columns={[
            {
              title: 'Row',
              dataIndex: 'rowNumber',
              key: 'rowNumber',
              width: 60,
            },
            {
              title: 'Case Name / Citation',
              dataIndex: 'case_name_citation',
              key: 'case_name_citation',
              width: 200,
              render: (text: string | null, record: ParsedReference) => text || record.case_name || 'N/A',
            },
            {
              title: 'Year',
              dataIndex: 'year',
              key: 'year',
              width: 80,
            },
            {
              title: 'Legal Issue',
              dataIndex: 'legal_issue',
              key: 'legal_issue',
              width: 150,
              render: (text: string | null, record: ParsedReference) => text || record.one_sentence_rule || 'N/A',
            },
            {
              title: 'Basis',
              dataIndex: 'basis',
              key: 'basis',
              width: 120,
              render: (text: string | null) => text ? <Tag>{text}</Tag> : '-',
            },
            {
              title: 'Case Type',
              dataIndex: 'case_type',
              key: 'case_type',
              width: 120,
              render: (text: string | null) => text ? <Tag color="green">{text}</Tag> : 'N/A',
            },
            {
              title: 'Opinion URL',
              dataIndex: 'opinion_url',
              key: 'opinion_url',
              width: 150,
              ellipsis: true,
              render: (url: string | null) => url ? (
                <a href={url} target="_blank" rel="noopener noreferrer">
                  <LinkOutlined /> View
                </a>
              ) : 'N/A',
            },
          ]}
          dataSource={parsedData}
          rowKey="rowNumber"
          pagination={{
            pageSize: 10,
            showSizeChanger: true,
            showTotal: (total) => `Total ${total} rows`,
          }}
          scroll={{ y: 400 }}
        />
      </Modal>
    </PageLayout>
  );
};

export default References;
